import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from "~src/hooks/useDocumentTitle";
import { timeFormatter } from '~src/utils/tool';
import { createSearchParams } from "react-router-dom";
import styles from './index.module.less';
import { Toast, Dialog } from 'antd-mobile/2x';
import { map_icon_start, map_icon_end, map_icon_direction } from "~src/constants/network";
import { beginCycling, submitRideInfo } from "~src/service/bike_ride";

interface Position {
  latitude: number;
  longitude: number;
}

export default React.memo(() => {
  const [isFlutter, setIsFlutter] = useState(false);
  const [watchPosition, setWatchPosition] = useState<boolean>(false);
  const [finish, setFinish] = useState<boolean>(false);
  const [distance, setDistance] = useState<number>(0);
  const [durationTime, setDurationTime] = useState<String>(timeFormatter(0));
  const [data, setData] = useState([
    {
      title: "减少CO",
      miniTitle: '2',
      value: 0,
      unit: 'g'
    },
    {
      title: "本次奖励",
      value: 0,
      unit: '积分'
    },
  ]);
  const mapRef = useRef<any>();
  const timer = useRef<NodeJS.Timer>();
  const rideStartTimeRef = useRef<number>(0);
  const polylineRef = useRef<any>();
  const markerStyleRef = useRef<any>();
  const locationRef = useRef<any>();
  const startRef = useRef<any>();
  const endRef = useRef<any>();
  const centerPositionRef = useRef<Position>();
  const lastPositionRef = useRef<Position>();
  const geosRef = useRef<Position[]>([]);

  useDocumentTitle("骑行减碳");

  useEffect(() => {
    const data = createSearchParams(window.location.search.split("?")[1] || "") || {};
    if (data.get("token") !== localStorage.getItem("mio-token")) {
      localStorage.setItem("mio-token", data.get("token") || "")
    }

    const event = (e: MessageEvent<any>) => {
      try {
        let data = null;
        try {
          data = JSON.parse(e.data ?? "");
        } catch (e) { }
        if (data.type === "updateLogs") {
          setMarket(data.location, locationRef, "location", "center");
          updateCenter(data.location);
          lastPositionRef.current = data.location;
          if (data.startTime > 0 && !timer.current) {
            if (document.title !== "骑行中") {
              window.flutter_inappwebview.callHandler('setTitle', "骑行中");
            }
            updateStartTime(data.startTime);
          }
          if (data.startTime === 0 && data.result) {
            updateMarker(data.result, false, data.logs);
          }
          updateLine(data.logs, );
          if (data.logs) {
            geosRef.current = data.logs;
          }
        }
        if (data.type === "flutterReady") {
          setIsFlutter(true);
          window.flutter_inappwebview.callHandler('setTitle', "骑行减碳");
        }
      } catch (e) { }
    }

    // listen for messages
    window.addEventListener('message', event, false);

    initMap();
    return () => {
      if (timer.current) {
        rideStartTimeRef.current = 0;
        clearInterval(timer.current);
        timer.current = undefined;
      }
      centerPositionRef.current = undefined;
      lastPositionRef.current = undefined;
      geosRef.current = [];
      window.removeEventListener('message', event, false);
    }
  }, [])

  const initMap = () => {
    if (mapRef.current) {
      return;
    }
    const toastContent = Toast.show(
      {
        icon: "loading",
      });
    try {
      mapRef.current = new AMap.Map("map", {
        zoom: 15,  //初始化地图层级
      });
    } catch (e) {
      console.log("初始化地图", e);
    }
    toastContent.close();
  }

  const initMarkerStyle = () => {
    if (markerStyleRef.current) {
      return;
    }
    markerStyleRef.current = {
      location: new AMap.Icon({
        size: new AMap.Size(22, 22),    // 图标尺寸
        image: map_icon_direction,  // Icon的图像
        imageOffset: new AMap.Pixel(0, 0),  // 图像相对展示区域的偏移量，适于雪碧图等
        imageSize: new AMap.Size(22, 22)   // 根据所设置的大小拉伸或压缩图片
      }),
      start: new AMap.Icon({
        size: new AMap.Size(40, 48),    // 图标尺寸
        image: map_icon_start,  // Icon的图像
        imageOffset: new AMap.Pixel(0, 0),  // 图像相对展示区域的偏移量，适于雪碧图等
        imageSize: new AMap.Size(40, 48)   // 根据所设置的大小拉伸或压缩图片
      }),
      end: new AMap.Icon({
        size: new AMap.Size(40, 48),    // 图标尺寸
        image: map_icon_end,  // Icon的图像
        imageOffset: new AMap.Pixel(0, -0),  // 图像相对展示区域的偏移量，适于雪碧图等
        imageSize: new AMap.Size(40, 48)   // 根据所设置的大小拉伸或压缩图片
      }),
    }
  }

  const updateCenter = (position: Position) => {
    if (position && position.latitude && position.longitude && !centerPositionRef.current) {
      if (mapRef.current) {
        mapRef.current.setCenter(new AMap.LngLat(position?.longitude, position?.latitude));
        centerPositionRef.current = position;
      }
    }
  }

  const setMarket = (position: Position | undefined, ref: React.MutableRefObject<any>, icon: string, anchor?: String) => {
    if (position && position.latitude && position.longitude) {
      initMarkerStyle();
      if (!ref.current) {
        // 创建标注对象并添加到地图
        ref.current = new AMap.Marker({
          position: new AMap.LngLat(position?.longitude, position?.latitude),
          anchor: anchor ?? 'bottom-center',
          offset: new AMap.Pixel(0, 0),
          icon: markerStyleRef.current?.[icon]
        });
        mapRef.current.add(ref.current);
      } else {
        ref.current.setPosition(new AMap.LngLat(position?.longitude, position?.latitude));
      }
    }
  }

  const updateLine = (positions: Position[]) => {
    if (positions && positions.length > 0) {
      setMarket(positions[0], startRef, 'start');
      if (!polylineRef.current) {
        polylineRef.current = new AMap.Polyline({
          path: positions.map((p) => (new AMap.LngLat(p.longitude, p.latitude))),
          borderWeight: 0, // 线条宽度，默认为 1
          strokeColor: "#23D85A", // 线条颜色
          strokeWeight: 6,
          strokeOpacity: 0.9,
          showDir: true,
          lineJoin: 'round' // 折线
        }
          // positions.map((p) => (new AMap.LngLat(p.longitude, p.latitude))), { strokeColor: "#23D85A", strokeWeight: 6 }
        );
        mapRef.current.add(polylineRef.current);
      } else {
        polylineRef.current.setPath(positions.map((p) => (new AMap.LngLat(p.longitude, p.latitude))));
      }
    }
  }

  const updateStartTime = async (time: number, position?: Position) => {
    const rideStartTime = time;
    rideStartTimeRef.current = rideStartTime;
    if(position) {
      setMarket(position, startRef, 'start');
    }
    if (timer.current) {
      rideStartTimeRef.current = 0;
      clearInterval(timer.current);
      timer.current = undefined;
    }
    setDurationTime(timeFormatter(0));
    timer.current = setInterval(() => {
      const duration = (new Date()).getTime() - (rideStartTime ?? 0);
      setDurationTime(timeFormatter(duration));
    }, 1000);
    setWatchPosition(true);
  }

  const updateMarker = async (result: { currentDistance: number, currentMinusCarbon: number, currentPoint: number, currentTime: number }, showToast = true, positions?: Position[]) => {
    const { currentDistance, currentMinusCarbon, currentPoint, currentTime } = result;
    try {
      setData([
        {
          title: "减少CO",
          miniTitle: '2',
          value: currentMinusCarbon,
          unit: 'g'
        },
        {
          title: "本次奖励",
          value: currentPoint,
          unit: '积分'
        },
      ]);
      setDistance(currentDistance);
      setDurationTime(timeFormatter(currentTime * 1000));
      if (positions && positions.length > 0) {
        setMarket(positions[0], startRef, 'start');
        setMarket(positions[positions.length - 1], endRef, 'end');
      } else {
        setMarket(geosRef.current[geosRef.current.length - 1], endRef, 'end');
      }

      if (timer.current) {
        rideStartTimeRef.current = 0;
        clearInterval(timer.current);
        timer.current = undefined;
      }
      setWatchPosition(false);
      setFinish(true);
    } catch (e) {
      Toast.show({
        content: JSON.stringify(e),
      });
    }
  }

  const getCurrentLocation = async () => {
    const permission = await checkPermission();
    if (!permission) {
      return;
    }
    centerPositionRef.current = undefined;
    if (lastPositionRef.current) {
      updateCenter(lastPositionRef.current);
    } else {
      Toast.show({
        content: "正在定位中...",
      });
    }
  }

  const goHome = async () => {
    if (window.flutter_inappwebview) {
      try {
        await window.flutter_inappwebview.callHandler('pushReplacementNamed', {
          path: "/",
        })
      } catch (e) { }
    }
  }

  const toggleWatchPosition = async () => {
    try {
      if (!watchPosition) {
        const permission = await checkPermission();
        if (!permission) {
          return;
        }
        try {
          const rideStartTime = (new Date()).getTime();
          if (lastPositionRef.current) {
            const toastContent = Toast.show({
              icon: "loading",
            });
            try {
              const result = await beginCycling();
              if(result) {
                try {
                  await Promise.all([
                    window.flutter_inappwebview.callHandler('setTitle', "骑行中"),
                    window.flutter_inappwebview.callHandler('startRiding', rideStartTime)
                  ]);
                } catch (e) {
                }
                toastContent.close();
                updateStartTime(rideStartTime, lastPositionRef.current);
              }
            }catch(err){
              toastContent.close();
            }
          } else {
            Toast.show({
              content: "正在定位中...",
            });
          }
        }catch (err){}
      } else {
        // 结束
        Dialog.confirm({
          style: {
            maxWidth: '100vw',
            zIndex: 1002
          },
          bodyStyle: {
            minWidth: 300,
            width: 300,
            margin: "auto"
          },
          content: '确认结束骑行吗？',
          cancelText: <div style={{ color: "#3C4044" }}>取消</div>,
          confirmText: <div style={{ color: "#23D85A" }}>确定</div>,
          onConfirm: async () => {
            try {
              const result = await submitRideInfo({
                startDate: rideStartTimeRef.current + '',
                endDate: (new Date()).getTime() + '',
                geos: geosRef.current
              })
              updateMarker(result);
              await Promise.all([
                window.flutter_inappwebview.callHandler('setTitle', "骑行减碳"),
                window.flutter_inappwebview.callHandler('endRiding', result)
              ]);
            } catch (e: any) {
              Toast.show({
                content: e.toString(),
              });
            }
          }
        })
      }
    } catch (e) {
      if (timer.current) {
        rideStartTimeRef.current = 0;
        clearInterval(timer.current);
        timer.current = undefined;
      }
    }

  }

  // 判断权限
  const checkPermission = async () => {
    if (!!window.flutter_inappwebview) {
      const permission = await window.flutter_inappwebview.callHandler('getPermission');
      if (!permission) {
        Toast.show({
          content: "获取定位失败，请检查定位权限",
        });
      }
      return permission;
    } else {
      Toast.show({
        content: "请在App中使用",
      });
      return false;
    }
  }

  return (
    <div className={styles.container}>
      <div id="map" className={styles.map} />
      {
        watchPosition ? <div className={styles.timerBox}>
          <div className={styles.time}>{durationTime}</div>
          <div className={styles.text}>骑行时长</div>
        </div> : null
      }
      {
        finish ? <div className={styles.finish}>
          <div className={styles.location} onClick={getCurrentLocation} />
          <div className={styles.img} />
          <div className={styles.time}>{durationTime}</div>
          <div className={styles.distance}>
            <div>本次已骑行</div>
            <div className={styles.distanceData}><div className={styles.num}>{distance}</div><div className={styles.unit}>公里</div></div>
          </div>
          <div className={styles.bottom}>
            <div className={styles.data}>{data.map(({
              title,
              miniTitle,
              value,
              unit
            }) => (<div className={styles.item}>
              <div className={styles.title}>{title}<span className={styles.mini}>{miniTitle}</span></div>
              <div className={styles.unit}><div className={styles.value}>{value}</div><div className={styles.unitText}>{unit}</div></div>
            </div>))}</div>
            <div className={classNames(styles.button)} onClick={goHome}>回首页</div>
          </div>
        </div> : <div className={styles.bottom}>
          <div className={styles.location} onClick={getCurrentLocation} />
          <div className={classNames([styles.text, watchPosition ? styles.red : ""])}>{watchPosition ? `骑行过程中请勿退出${isFlutter ? "App" : "小程序"}` : "骑行1公里可获得20积分"}</div>
          <div className={classNames([styles.button, watchPosition ? styles.red : ""])} onClick={toggleWatchPosition}>{watchPosition ? "结束骑行" : "开始骑行"}</div>
        </div>
      }
    </div>)
});
